import React, { FunctionComponent, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { nFormatter } from 'app/utils/numbersFormater';
import { _t } from 'app/utils/i18n';
import NoData from 'app/components/NoData';
import { checkTranslated, removeHash } from 'app/utils/helpers';
import { useAppSettings } from 'app/context/settings/appSettings';

const imagesMap = {
  [_t('basic:facebook')]:
    `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><g transform="translate(-600.5 -611.5)"><g transform="translate(601 612)"><path d="M15,14.062a.938.938,0,0,1-.937.937H.938A.938.938,0,0,1,0,14.062V.937A.938.938,0,0,1,.938,0H14.063A.938.938,0,0,1,15,.937Z" transform="translate(0)" fill="none" stroke="var(--mainColor)" stroke-width="1"/><path d="M17.219,17.656V12.031h1.875l.469-2.344H17.219V8.75a1.243,1.243,0,0,1,1.406-1.406h.938V5H17.688c-1.723,0-2.812,1.35-2.812,3.281V9.687H13v2.344h1.875v5.625Z" transform="translate(-6.906 -2.656)" fill="var(--mainColor)"/></g></g></svg>`,
  [_t('basic:twitter')]:
    `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="13.188" viewBox="0 0 16 13.188"><g transform="translate(0.5 -47.487)"><path d="M4.717,60.175a8.7,8.7,0,0,0,8.756-8.756c0-.133,0-.266-.009-.4A6.262,6.262,0,0,0,15,49.428a6.144,6.144,0,0,1-1.768.484,3.089,3.089,0,0,0,1.353-1.7,6.168,6.168,0,0,1-1.954.747,3.08,3.08,0,0,0-5.245,2.807,8.741,8.741,0,0,1-6.343-3.215A3.093,3.093,0,0,0,2,52.657a3.054,3.054,0,0,1-1.4-.385v.039a3.088,3.088,0,0,0,2.469,3.017,3.073,3.073,0,0,1-1.39.053,3.087,3.087,0,0,0,2.875,2.137,6.177,6.177,0,0,1-3.822,1.32A6.26,6.26,0,0,1,0,58.793a8.713,8.713,0,0,0,4.717,1.38" fill="none" stroke="var(--mainColor)" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"/></g></svg>`,
  [_t('basic:instagram')]:
    `<svg xmlns="http://www.w3.org/2000/svg" width="15.999" height="15.999" viewBox="0 0 15.999 15.999"><g transform="translate(-2068.329 -262.81)"><path d="M2083.775,267.113a5.067,5.067,0,0,0-.315-1.7,3.032,3.032,0,0,0-1.736-1.736,5.062,5.062,0,0,0-1.7-.315c-.965-.044-1.254-.053-3.7-.053s-2.732.009-3.7.053a5.062,5.062,0,0,0-1.7.315,3.029,3.029,0,0,0-1.736,1.736,5.066,5.066,0,0,0-.315,1.7c-.044.965-.053,1.254-.053,3.7s.009,2.732.053,3.7a5.065,5.065,0,0,0,.315,1.7,3.029,3.029,0,0,0,1.736,1.736,5.062,5.062,0,0,0,1.7.315c.965.044,1.254.053,3.7.053s2.732-.009,3.7-.053a5.062,5.062,0,0,0,1.7-.315,3.032,3.032,0,0,0,1.736-1.736,5.067,5.067,0,0,0,.315-1.7c.044-.965.053-1.254.053-3.7S2083.819,268.078,2083.775,267.113Zm-3.365.87a1.1,1.1,0,1,1,1.1-1.1A1.1,1.1,0,0,1,2080.41,267.983Z" fill="none" stroke="var(--mainColor)" stroke-miterlimit="10" stroke-width="1"/><circle cx="3.049" cy="3.049" r="3.049" transform="translate(2073.279 267.76)" fill="var(--mainColor)"/></g></svg>`,
  [_t('basic:youtube')]:
    `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="11.5" viewBox="0 0 16 11.5"><g transform="translate(-1026.811 -628.016)"><g transform="translate(1027.311 628.516)"><path d="M14.687,78.441a1.878,1.878,0,0,0-1.326-1.326A44.819,44.819,0,0,0,7.5,76.8a44.82,44.82,0,0,0-5.86.314A1.879,1.879,0,0,0,.313,78.441a20.955,20.955,0,0,0,0,7.221,1.879,1.879,0,0,0,1.326,1.326A44.837,44.837,0,0,0,7.5,87.3a44.837,44.837,0,0,0,5.86-.313,1.878,1.878,0,0,0,1.326-1.326A19.515,19.515,0,0,0,15,82.051,19.516,19.516,0,0,0,14.687,78.441Z" transform="translate(0 -76.801)" fill="none" stroke="var(--mainColor)" stroke-width="1"/></g><path d="M204.8,183.7l3.9-2.25-3.9-2.25Z" transform="translate(828.514 452.315)" fill="var(--mainColor)"/></g></svg>`,
};

const renderPlatformIcon = (platform: string) => {
  platform = platform.toLowerCase();
  return imagesMap[platform as any];
};

interface Prop {
  stackedSeries: { name: string; data: number[] }[];
  categories?: string[];
  isHorizontal?: boolean;
  showLegend?: boolean;
  showToolbar?: boolean;
  customTooltip?: any;
  legendPosition?: string;
  customSeriesTitle?: any;
  showToolTip?: boolean;
  compId: string;
  stackType?: boolean;
  distributed?: boolean;
  compeletLabelsText?: boolean;
  withLegendCount?: boolean;
  noLegendPercentage?: boolean;
}
const StackedColumns: FunctionComponent<Prop> = ({
  stackedSeries,
  categories,
  isHorizontal,
  showLegend,
  showToolbar,
  customTooltip,
  customSeriesTitle,
  legendPosition,
  showToolTip,
  compId,
  stackType,
  distributed,
  compeletLabelsText,
  withLegendCount,
  noLegendPercentage,
}) => {
  const { theme } = useAppSettings();
  if (!stackedSeries?.[0]?.data) {
    return <NoData />;
  }

  return (
    <div id={compId} data-testid={`${compId}-testid`} style={{ minHeight: '200px', width: '100%' }}>
      <ReactApexChart
        options={getStackedColumnsOptions(
          theme.colors.chartsColors,
          categories,
          isHorizontal,
          // isTime,
          showLegend,
          showToolbar,
          customTooltip,
          customSeriesTitle,
          legendPosition,
          showToolTip,
          stackType,
          distributed,
          compeletLabelsText,
          withLegendCount,
          noLegendPercentage,
          // subCategoryTooltip
          // colors
        )}
        series={stackedSeries}
        type="bar"
        height="100%"
        data-testid="stackedColumnchart"
      />
    </div>
  );
};

export default StackedColumns;

function getStackedColumnsOptions(
  colors: string[],
  categories?: string[],
  isHorizontal?: boolean,
  showLegend?: boolean,
  showToolbar?: boolean,
  customTooltip?: any,
  customSeriesTitle?: any,
  legendPosition?: string,
  showToolTip?: boolean,
  stackType?: boolean,
  distributed?: boolean,
  compeletLabelsText?: boolean,
  withLegendCount?: boolean,
  noLegendPercentage?: boolean,
): any {

  const options = {
    colors: colors,
    chart: {
      type: 'bar',
      // height: 350,
      stacked: true,
      stackType: stackType ? '100%' : '',
      toolbar: {
        show: showToolbar,
        offsetX: 5,
        offsetY: 25,
      },
      zoom: {
        enabled: true,
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: 'bottom',
            offsetX: -10,
            offsetY: 0,
          },
        },
      },
    ],
    plotOptions: {
      bar: {
        borderRadius: 8,
        // horizontal: false,
        distributed: distributed,
        horizontal: isHorizontal,
      },
    },

    xaxis: {
      // type: isTime ? 'time' : 'datetime',
      type: 'category',
      categories: categories,
      labels: {
        formatter: function (value: any) {
          const newValue =
            typeof value === 'string'
              ? value
              : checkTranslated(value?.original, value?.arabic);
          return newValue && newValue.length > 5
            ? compeletLabelsText
              ? newValue.slice(0, 10)
              : newValue.slice(0, 5) + '...'
            : newValue;
        },
      },
    },
    yaxis: {
      show: true,
      labels: {
        formatter: (value: any) => {
          if (!isHorizontal) return nFormatter(Math.ceil(value));
          else
            return value && value.length > 5
              ? removeHash(value.slice(0, 5)) + '...'
              : value;
        },
      },
    },
    dataLabels: {
      enabled: false,
      textAnchor: 'end',
      style: {
        colors: ['#fff'],
      },
      // formatter: function (val: any, opt: any) {
      //   return opt.w.globals.labels[opt.dataPointIndex] + ":  " + val;
      // },
      offsetY: 0,
      offsetX: 200,
      dropShadow: {
        enabled: true,
      },
    },
    legend: {
      show: showLegend,
      fontSize: '10px',
      PointerEvent: 'unset',
      fontFamily: 'LatoRegular, sans-serif',
      position: legendPosition ? legendPosition : 'bottom',
      labels: {
        colors: undefined,
        useSeriesColors: false,
      },
      onItemClick: {
        toggleDataSeries: false,
      },
      onItemHover: {
        highlightDataSeries: true,
      },
      markers: {
        width: 12,
        height: 12,
      },
      itemMargin: {
        horizontal: 5,
        vertical: 10,
      },
      formatter: function (seriesName: string, opts: any) {
        if (seriesName.length > 20)
          seriesName = seriesName.slice(0, 16) + '...';
        const percentage =
          opts?.w?.globals?.seriesPercent?.[opts.seriesIndex]?.[0]?.toFixed(1);
        const value = opts?.w?.globals?.series?.[0]?.[opts.seriesIndex];
        return withLegendCount
          ? `${seriesName} (${value})`
          : noLegendPercentage
            ? seriesName
            : seriesName + ' ' + percentage + '%';
      },
    },
    fill: {
      opacity: 1,
    },
    // dataLabels: {
    //   enabled: false,
    // },
    grid: {
      show: false,
    },
    tooltip: {
      enabled: showToolTip ? true : false,
      custom: showToolTip ? customTooltip : undefined,
      shared: showToolTip ? true : false,

      intersect: false,
      // shared: true,
      marker: {
        show: true,
      },
      // x: {
      //   show: true,
      //   // format: 'dd MMM',
      //   formatter: function (val: any, opt: any) {
      //     return `${nFormatter(val)}` },
      // },
      y: {
        title: {
          formatter: (seriesName: any) => {
            return customSeriesTitle
              ? renderPlatformIcon(seriesName)
              : seriesName;
          },
        },
      },
    },
  };
  return options;
}
